import { useMemo } from "react"
import { IconUserXmarkOutline18 as UserX } from "nucleo-ui-outline-18"

import type { FilterType } from "./filter-popover-items/types"
import type { SearchableFilterItem } from "@/components/filters"
import { useFilteredSearchItems } from "@/components/filters"
import { SOURCE_OPTIONS } from "@/components/filters/source-options"
import { api } from "@/lib/api"
import { useCompanyAttributeValues } from "@/lib/use-company-attribute-values"
import { useZeroWorkspace } from "@/lib/zero"
import { Owner } from "@/screens/app/threads/components/right-bar/customer-properties/icons/owner"
import { FILTER_CATEGORIES } from "./companies-filter"
import { useCompaniesFilterState } from "./companies-filter-state"

const CATEGORY_ORDER: FilterType[] = [
	"Statuses",
	"Tiers",
	"Owners",
	"Revenue",
	"Size",
	"CreatedBy",
]

export function useCompaniesSearchableItems({
	onCategorySelect,
}: {
	onCategorySelect: (type: FilterType) => void
}): SearchableFilterItem<FilterType>[] {
	const workspace = useZeroWorkspace()
	const { tiers, statuses } = useCompanyAttributeValues()
	const filterState = useCompaniesFilterState()

	const teamId = workspace?.defaultLinearTeamId ?? ""
	const membersQuery = api.linear.members.useQuery(
		{ teamId },
		{ enabled: !!teamId, cacheTime: Infinity, staleTime: Infinity },
	)
	const members = membersQuery.data ?? []

	return useMemo((): SearchableFilterItem<FilterType>[] => {
		const items: SearchableFilterItem<FilterType>[] = []

		FILTER_CATEGORIES.forEach((cat) => {
			items.push({
				id: `category-${cat.type}`,
				category: cat.type,
				categoryLabel: cat.label,
				label: cat.label,
				searchText: cat.label,
				Icon: cat.Icon,
				onSelect: () => onCategorySelect(cat.type),
				isSelected: false,
			})
		})

		tiers.forEach((tier) => {
			items.push({
				id: `tier-${tier}`,
				category: "Tiers",
				categoryLabel: "Tier",
				label: tier,
				searchText: `Tier ${tier}`,
				onSelect: () => {
					if (filterState.tiers.includes(tier)) {
						filterState.removeTier(tier)
					} else {
						filterState.addTier(tier)
					}
				},
				isSelected: filterState.tiers.includes(tier),
			})
		})

		statuses
			.map((status) => [status.name, status.color] as const)
			.sort(([a], [b]) => a.localeCompare(b))
			.forEach(([name, color]) => {
				items.push({
					id: `status-${name}`,
					category: "Statuses",
					categoryLabel: "Status",
					label: name,
					searchText: `Status ${name}`,
					onSelect: () => {
						if (filterState.statuses.includes(name)) {
							filterState.removeStatus(name)
						} else {
							filterState.addStatus(name)
						}
					},
					isSelected: filterState.statuses.includes(name),
					renderCustom: () => (
						<div
							className="h-2 w-2 shrink-0 rounded-full"
							style={{ backgroundColor: color }}
						/>
					),
				})
			})

		items.push({
			id: "owner-none",
			category: "Owners",
			categoryLabel: "Owner",
			label: "No owner",
			searchText: "Owner No owner",
			Icon: UserX,
			onSelect: () => {
				if (filterState.ownerIds.includes(null)) {
					filterState.removeOwner(null)
				} else {
					filterState.addOwner(null)
				}
			},
			isSelected: filterState.ownerIds.includes(null),
		})

		members.forEach((member) => {
			const avatarUrl = member.avatarUrl
			items.push({
				id: `owner-${member.id}`,
				category: "Owners",
				categoryLabel: "Owner",
				label: member.displayName,
				searchText: `Owner ${member.displayName}`,
				Icon: Owner,
				onSelect: () => {
					if (filterState.ownerIds.includes(member.id)) {
						filterState.removeOwner(member.id)
					} else {
						filterState.addOwner(member.id)
					}
				},
				isSelected: filterState.ownerIds.includes(member.id),
				renderCustom: avatarUrl
					? () => (
							<img
								src={avatarUrl}
								alt={member.displayName}
								className="h-4 w-4 shrink-0 rounded-full object-cover"
							/>
						)
					: undefined,
			})
		})

		SOURCE_OPTIONS.forEach(({ value, label, Icon }) => {
			items.push({
				id: `source-${value}`,
				category: "CreatedBy",
				categoryLabel: "Synced with",
				label,
				searchText: `Synced with ${label}`,
				Icon,
				onSelect: () => {
					if (filterState.sources.includes(value)) {
						filterState.removeSource(value)
					} else {
						filterState.addSource(value)
					}
				},
				isSelected: filterState.sources.includes(value),
			})
		})

		return items
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tiers, statuses, members, filterState, onCategorySelect])
}

export function useFilteredCompaniesSearchItems(
	globalQuery: string,
	allItems: SearchableFilterItem<FilterType>[],
	filterState: {
		revenue: { operator: string; value: number } | null
		setRevenue: (
			filter: { operator: ">=" | "<=" | "=" | "!="; value: number } | null,
		) => void
		size: { operator: string; value: number } | null
		setSize: (
			filter: { operator: ">=" | "<=" | "=" | "!="; value: number } | null,
		) => void
	},
) {
	return useFilteredSearchItems<FilterType>(globalQuery, allItems, {
		categoryOrder: CATEGORY_ORDER,
		extraItems: (q) => {
			const numericValue = Number(q.trim())
			if (isNaN(numericValue) || !q.trim()) return []
			const operators: { operator: ">=" | "<=" | "=" | "!="; label: string }[] =
				[
					{ operator: ">=", label: "Greater than or equal to" },
					{ operator: "<=", label: "Less than or equal to" },
					{ operator: "=", label: "Equal to" },
					{ operator: "!=", label: "Not equal to" },
				]
			const numericItems: SearchableFilterItem<FilterType>[] = []
			for (const { operator, label } of operators) {
				numericItems.push({
					id: `revenue-${operator}-${numericValue}`,
					category: "Revenue",
					categoryLabel: "Revenue",
					label: `${label} $${numericValue}`,
					searchText: `Revenue ${label} ${numericValue}`,
					onSelect: () =>
						filterState.setRevenue({ operator, value: numericValue }),
					isSelected:
						filterState.revenue?.operator === operator &&
						filterState.revenue?.value === numericValue,
				})
				numericItems.push({
					id: `size-${operator}-${numericValue}`,
					category: "Size",
					categoryLabel: "Size",
					label: `${label} ${numericValue}`,
					searchText: `Size ${label} ${numericValue}`,
					onSelect: () =>
						filterState.setSize({ operator, value: numericValue }),
					isSelected:
						filterState.size?.operator === operator &&
						filterState.size?.value === numericValue,
				})
			}
			return numericItems
		},
	})
}
